/**
 * Items v2 smith actions (docs/systems/ITEMS_V2.md §2, R3–R4), as data: the materials, the gems and every cost. All costs
 * are ตำลึง (`progress.gold`) plus materials: sinks only, nothing here is bought with หยกเซียน (docs/systems/ECONOMY.md).
 * The rules are systems/itemCraft.ts.
 */
import type { ItemStats, Rarity } from './items';
import type { GoodDef } from './professions';

/** Materials of the smith's actions (goods: they stack, trade and sell like any material). */
export const CRAFT_MATS = {
  reroll: 'reroll-stone',
  hone: 'hone-dust',
  seal: 'seal-mark',
  drill: 'drill-stone',
  /** ยันต์กันถอย: stops the slip of one failed strike (+7 and up). Shops only, never a drop. */
  protect: 'protect-charm',
} as const;

// ── gems (ฝังหยก) ──

export interface GemKind {
  readonly id: string;
  readonly name: string;
  /** What one grade adds. */
  readonly stat: keyof ItemStats;
  readonly per: number;
}

export const GEM_KINDS: readonly GemKind[] = [
  { id: 'ruby', name: 'ทับทิมโลหิต', stat: 'atk', per: 2 },
  { id: 'sapphire', name: 'ไพลินคราม', stat: 'magic', per: 2 },
  { id: 'jade', name: 'หยกเขียว', stat: 'maxHp', per: 20 },
  { id: 'amber', name: 'อำพันทอง', stat: 'def', per: 2 },
  { id: 'pearl', name: 'ไข่มุกจันทร์', stat: 'maxQi', per: 10 },
];
export const GEM_MAX_GRADE = 5;
/** Gems of one grade that merge into one of the next. */
export const GEM_MERGE = 3;

export const gemId = (kind: string, grade: number): string => `gem-${kind}-${grade}`;

/** A gem good's kind and grade, or null. */
export function gemOf(id: string): { kind: GemKind; grade: number } | null {
  const m = /^gem-([a-z]+)-(\d)$/.exec(id);
  if (!m) return null;
  const kind = GEM_KINDS.find((k) => k.id === m[1]);
  const grade = Number(m[2]);
  return kind && grade >= 1 && grade <= GEM_MAX_GRADE ? { kind, grade } : null;
}

/** What a gem in a socket adds. */
export function gemStats(id: string): ItemStats {
  const g = gemOf(id);
  return g ? { [g.kind.stat]: g.kind.per * g.grade } : {};
}

const GRADE_NAMES = ['', 'หยาบ', 'ธรรมดา', 'ดี', 'ล้ำค่า', 'ไร้ตำหนิ'];

/** The materials and gems as goods (data/professions.ts lists them after its own). */
export const CRAFT_GOODS: readonly GoodDef[] = [
  {
    id: CRAFT_MATS.protect,
    name: 'ยันต์กันถอย',
    kind: 'material',
    price: 150,
    desc: 'ยันต์ที่ใช้ตอนตีบวกตั้งแต่ +7 ขึ้นไป ถ้าตีพลาดจะไม่ถอยระดับ ใช้ได้ครั้งเดียว แลกได้จากร้านประลองและร้านกิลด์เท่านั้น',
  },
  {
    id: CRAFT_MATS.reroll,
    name: 'หินล้างค่า',
    kind: 'material',
    price: 20,
    desc: 'หินสีหมอกที่ช่างตีเหล็กใช้ล้างค่าพลังหนึ่งบรรทัดของอุปกรณ์ให้สุ่มใหม่',
  },
  {
    id: CRAFT_MATS.hone,
    name: 'ผงเกลา',
    kind: 'material',
    price: 8,
    desc: 'ผงหินละเอียดสำหรับขัดเกลาค่าพลังให้คุณภาพเปลี่ยนไป ได้จากการแยกชิ้นอุปกรณ์',
  },
  {
    id: CRAFT_MATS.seal,
    name: 'ตราผนึก',
    kind: 'material',
    price: 60,
    desc: 'ตราประทับที่ผนึกค่าพลังไว้ไม่ให้ถูกล้าง ได้จากบอสและการแยกชิ้นของหายาก',
  },
  {
    id: CRAFT_MATS.drill,
    name: 'หินเจาะ',
    kind: 'material',
    price: 50,
    desc: 'หินแข็งปลายแหลมสำหรับเจาะรูฝังหยกเพิ่มบนอุปกรณ์',
  },
  // The world bosses' chest materials (data/worldBosses.ts `materials`): หลอมยกระดับ for their gear.
  ...(
    [
      ['wb-silver-fur', 'ขนเงินวานรยักษ์', 'ขนสีเงินของลิงยักษ์ทงปี้ เหนียวกว่าเชือกป่าน'],
      ['wb-ape-fang', 'เขี้ยววานรยักษ์', 'เขี้ยวของลิงยักษ์ทงปี้ แข็งพอจะบิ่นดาบ'],
      ['wb-fire-feather', 'ขนนกเพลิง', 'ขนของนกเพลิงปี้ฟาง ยังอุ่นแม้หลุดจากตัวมานาน'],
      ['wb-eternal-ash', 'เถ้านิรันดร์', 'เถ้าของนกเพลิงที่ไม่มีวันเย็นสนิท'],
      ['wb-thunder-scale', 'เกล็ดอสนี', 'เกล็ดกระดองเต่าคะนอง มีประกายไฟฟ้าวิ่งตามลาย'],
      ['wb-thunder-pearl', 'ไข่มุกฟ้าคำราม', 'ไข่มุกจากท้องเต่าคะนอง ได้ยินเสียงฟ้าร้องแผ่วๆ'],
    ] as const
  ).map(
    ([id, name, desc]): GoodDef => ({
      id,
      name,
      kind: 'material',
      price: 40,
      desc: `${desc} ใช้หลอมยกระดับอุปกรณ์ของบอสตนนี้ที่ช่างตีเหล็ก`,
    }),
  ),
  ...GEM_KINDS.flatMap((k) =>
    Array.from({ length: GEM_MAX_GRADE }, (_, i): GoodDef => {
      const grade = i + 1;
      return {
        id: gemId(k.id, grade),
        name: `${k.name} (${GRADE_NAMES[grade]})`,
        kind: 'material',
        price: 10 * 3 ** i,
        desc: `ฝังในรูของอุปกรณ์ที่ช่างตีเหล็ก ${GEM_MERGE} เม็ดขั้นเดียวกันรวมเป็นขั้นถัดไปได้`,
      };
    }),
  ),
];

// ── sockets ──

/** Sockets an item may hold, by rarity (เจาะรู up to this). */
export const MAX_SOCKETS: Readonly<Record<Rarity, number>> = {
  common: 0,
  fine: 0,
  rare: 1,
  epic: 2,
  legend: 2,
  mythic: 3,
};
/** Sockets a fresh roll comes with: a fixed count, or rare's chance of one. */
export const START_SOCKETS: Readonly<Record<Rarity, number>> = {
  common: 0,
  fine: 0,
  rare: 0,
  epic: 1,
  legend: 2,
  mythic: 2,
};
export const RARE_SOCKET_CHANCE = 0.3;

// ── costs (ตำลึง) ──

export const CRAFT_COST = {
  /** ล้างค่า: (base + per ilv) × 2^rerolls on the item (up to `rerollDoublings`) × 2 per lock; 1 หินล้างค่า, 1 ตราผนึก per lock. */
  reroll: { base: 40, perIlv: 8 },
  rerollDoublings: 6,
  /** ขัดเกลา: base + per ilv, 1 ผงเกลา. */
  hone: { base: 20, perIlv: 4 },
  /** Most affixes locked at once. */
  maxLocks: 2,
  /** ถอดหยก: per grade (the gem comes back). */
  unsocketPerGrade: 50,
  /** เจาะรู: base + per ilv, 1 หินเจาะ. */
  drill: { base: 100, perIlv: 10 },
  /** รวมหยก: per grade of the gems merged. */
  mergePerGrade: 30,
} as const;

/** What แยกชิ้น gives, by rarity: goods id → count. */
export const SALVAGE: Readonly<Record<Rarity, Readonly<Record<string, number>>>> = {
  common: { [CRAFT_MATS.hone]: 1 },
  fine: { [CRAFT_MATS.hone]: 2 },
  rare: { [CRAFT_MATS.hone]: 2, [CRAFT_MATS.reroll]: 1 },
  epic: { [CRAFT_MATS.hone]: 3, [CRAFT_MATS.reroll]: 2, [CRAFT_MATS.drill]: 1 },
  legend: { [CRAFT_MATS.hone]: 4, [CRAFT_MATS.reroll]: 3, [CRAFT_MATS.seal]: 1, [CRAFT_MATS.drill]: 1 },
  mythic: { [CRAFT_MATS.hone]: 6, [CRAFT_MATS.reroll]: 4, [CRAFT_MATS.seal]: 2, [CRAFT_MATS.drill]: 2 },
};

export const rerollCost = (ilv: number, rerolls: number, locks: number): number =>
  (CRAFT_COST.reroll.base + CRAFT_COST.reroll.perIlv * ilv) *
  2 ** Math.min(rerolls, CRAFT_COST.rerollDoublings) *
  2 ** locks;
export const honeCost = (ilv: number): number => CRAFT_COST.hone.base + CRAFT_COST.hone.perIlv * ilv;
export const drillCost = (ilv: number): number => CRAFT_COST.drill.base + CRAFT_COST.drill.perIlv * ilv;

/** หลอมยกระดับ (docs/systems/ITEMS_V2.md §2): +`step` item levels up to the source's cap; ตำลึง per ilv and the source's materials. */
export const RAISE = {
  step: 5,
  goldPerIlv: 50,
  /** Levels over its source a piece may be raised to: a boss's gear to the boss's level + this, other gear to its own base level + `other`. */
  overBoss: 15,
  other: 10,
  /** Materials per raise: the boss's first chest material, or these for other gear. */
  bossMats: 2,
  otherMats: { [CRAFT_MATS.hone]: 2, [CRAFT_MATS.reroll]: 1 } as Readonly<Record<string, number>>,
} as const;
export const raiseCost = (ilv: number): number => RAISE.goldPerIlv * ilv;

// ── sources (docs/systems/ITEMS_V2.md §3) ──

/** Materials (goods id → count) and gems (one random kind per entry, at that grade) a source pays. */
export interface CraftLoot {
  readonly mats: Readonly<Record<string, number>>;
  readonly gems: readonly number[];
}

/**
 * What the big activities pay on top of their own rewards (systems/itemCraft.ts `observe`): a world boss's full chest
 * by tier, a rewarded dungeon chest, a won raid and a fair. Monster drops are in data/drops.ts.
 */
export const CRAFT_SOURCES: {
  readonly worldBoss: Readonly<Record<'gold' | 'silver' | 'copper', CraftLoot>>;
  readonly dungeon: CraftLoot;
  readonly raid: CraftLoot;
  readonly fair: CraftLoot;
} = {
  worldBoss: {
    gold: { mats: { [CRAFT_MATS.seal]: 2, [CRAFT_MATS.drill]: 1, [CRAFT_MATS.reroll]: 2 }, gems: [2, 2] },
    silver: { mats: { [CRAFT_MATS.seal]: 1, [CRAFT_MATS.reroll]: 2 }, gems: [1, 1] },
    copper: { mats: { [CRAFT_MATS.reroll]: 1, [CRAFT_MATS.hone]: 2 }, gems: [1] },
  },
  dungeon: { mats: { [CRAFT_MATS.drill]: 1, [CRAFT_MATS.reroll]: 1, [CRAFT_MATS.hone]: 2 }, gems: [1, 1] },
  raid: { mats: { [CRAFT_MATS.reroll]: 1, [CRAFT_MATS.hone]: 2 }, gems: [1] },
  fair: { mats: { [CRAFT_MATS.hone]: 3 }, gems: [1] },
};
